import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Company, CompanyFormData } from '@/types/crm';

interface CompanyStore {
  companies: Company[];
  selectedCompany: Company | null;
  isLoading: boolean;
  searchTerm: string;
  fetchCompanies: () => Promise<void>;
  fetchCompanyById: (id: string) => Promise<Company | null>;
  createCompany: (data: CompanyFormData) => Promise<Company | null>;
  updateCompany: (id: string, data: Partial<CompanyFormData>) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;
  setSelectedCompany: (company: Company | null) => void;
  setSearchTerm: (term: string) => void;
}

export const useCompanyStore = create<CompanyStore>((set, get) => ({
  companies: [],
  selectedCompany: null,
  isLoading: false,
  searchTerm: '',

  fetchCompanies: async () => {
    set({ isLoading: true });
    try {
      const { searchTerm } = get();
      let query = supabase
        .from('crm_companies')
        .select('*')
        .order('name', { ascending: true });

      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,industry.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      set({ companies: data || [] });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCompanyById: async (id: string) => {
    const { data, error } = await supabase
      .from('crm_companies')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) return null;
    set({ selectedCompany: data });
    return data;
  },

  createCompany: async (data: CompanyFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: company, error } = await supabase
      .from('crm_companies')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    await get().fetchCompanies();
    return company;
  },

  updateCompany: async (id: string, data: Partial<CompanyFormData>) => {
    const { error } = await supabase
      .from('crm_companies')
      .update(data)
      .eq('id', id);

    if (error) throw error;
    await get().fetchCompanies();
  },

  deleteCompany: async (id: string) => {
    const { error } = await supabase
      .from('crm_companies')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await get().fetchCompanies();
  },

  setSelectedCompany: (company: Company | null) => set({ selectedCompany: company }),
  setSearchTerm: (term: string) => set({ searchTerm: term }),
}));
